/** Safe JSON parsing and stringifying. The parse core lives in `const/json-parse.ts` so this leaf can share it without a forbidden utils->utils value import. */

import { parseJsonSafe } from '@federa/const/json-parse';
import { isRecordValue } from '@federa/const/record-guards';

/** Guard-param overload: parse AND narrow in one call, yielding `defaultValue` on anything failing the predicate -- the CAST-FREE form, a checked narrowing instead of an `as` asserting a shape nothing verified. The predicate is a PARAMETER because this leaf sits on the utils purity floor and cannot import the type-guard predicates itself. */
export function safeJsonParse<T, D>(
	str: unknown,
	defaultValue: D,
	options: { guard: (value: unknown) => value is T; preserveLargeIntegers?: boolean },
): T | D;
/** Parse a JSON string, quoting integers past MAX_SAFE_INTEGER first so large IDs do not lose precision. `defaultValue` is the CALLER's own type parameter, not `unknown` (QUAL-UNKNOWN-PARAM-001): it is returned verbatim, and real call sites include GENERIC forwarders a closed union would reject. */
export function safeJsonParse<D>(str: unknown, defaultValue?: D, options?: { preserveLargeIntegers?: boolean }): unknown;
export function safeJsonParse(
	str: unknown,
	defaultValue: unknown = null,
	options: { preserveLargeIntegers?: boolean; guard?: (value: unknown) => boolean } = {},
): unknown {
	const { preserveLargeIntegers = true, guard } = options;
	/** One candidate answer read through the caller's guard: a value the guard rejects is a MISS, so the default
	 *  stands. The passthrough and the parsed result are screened identically, which is the whole contract. */
	const guarded = (value: unknown): unknown => (guard && !guard(value) ? defaultValue : value);

	if (str === null) return defaultValue;
	if (str === undefined) return defaultValue;
	// The passthrough is GATED under a guard too: the caller asked for `T`, so a structured input of another shape is a miss.
	if (typeof str !== 'string') return guarded(str);
	if (str === '') return defaultValue;

	return guarded(parseJsonSafe(str, defaultValue, preserveLargeIntegers));
}

/** Parse `value` when it arrives as a JSON string, else pass it through -- canonical guard for dual-shape payloads (D1 TEXT columns, flow inputs). */
export function parseIfString<T>(value: string | T, fallback?: T): T {
	return typeof value === 'string' ? (safeJsonParse(value, fallback) as T) : value;
}

/** Shared array-column parse core, parameterized over the fallback shape and large-integer preservation. A bracket pre-guard is intentionally OMITTED: the parse core is plain `JSON.parse`, so a non-array or malformed string already falls through to `fallback` either way. */
function coerceJsonToArray<T>(raw: unknown, fallback: T[] | null, preserveLargeIntegers: boolean): T[] | null {
	if (typeof raw !== 'string') return fallback;
	const parsed = parseJsonSafe(raw, null, preserveLargeIntegers);
	return Array.isArray(parsed) ? (parsed as T[]) : fallback;
}

/** Try to parse a JSON-encoded array string, returning null when the input is not a JSON array. */
export function tryParseJsonArray(value: unknown): unknown[] | null {
	return coerceJsonToArray<unknown>(value, null, false);
}

/** JSON.stringify wrapper returning `defaultValue` on throw (circular refs, bigints without a replacer). */
export function stringifyWithDefault(value: unknown, defaultValue: string, replacer?: (key: string, value: unknown) => unknown): string {
	try {
		return JSON.stringify(value, replacer);
	} catch {
		return defaultValue;
	}
}

/** Circular-reference-safe stringify: bigints coerce to strings, and a value that is its own ANCESTOR collapses to
 *  '[Circular]'.
 *
 *  The guard tracks the current PATH, not every object seen. That distinction is the whole point: a repeated but
 *  non-cyclic reference (the same object under two keys, or twice in an array) is a DAG, not a cycle, and must
 *  serialize in full at each position. A seen-set collapses the second occurrence to '[Circular]', which is a FALSE
 *  statement about the data rather than a formatting choice -- and callers persist this output to D1
 *  (`flows/user-activity-save.ts`) and POST it over the wire (`tools/flowrunner-cli/cli-tracker-writer.ts`), so the
 *  loss is unrecoverable downstream and indistinguishable from a genuine cycle.
 *
 *  `this` is the container the current key was reached through, so popping back to it before each push keeps
 *  `ancestors` equal to the chain from the root. */
export function safeStringify(value: unknown, defaultValue = '{}'): string {
	const ancestors: unknown[] = [];
	return stringifyWithDefault(value, defaultValue, function (this: unknown, _key: string, val: unknown) {
		if (typeof val === 'bigint') return String(val);
		if (!isRecordValue(val)) return val;
		while (ancestors.length > 0 && ancestors.at(-1) !== this) ancestors.pop();
		if (ancestors.includes(val)) return '[Circular]';
		ancestors.push(val);
		return val;
	});
}
